import { CommonModule } from "@angular/common";
import { Component, OnInit, inject, signal } from "@angular/core";
import { MiproSharedModule } from "@mipro/mipro-shared.module";
import { ButtonModule } from "primeng/button";
import { NamingConventionService } from "@mipro/services/naming-convention.service";
import { MessagesModule } from "primeng/messages";
import { MergePatientsDataService } from "../merge-patient/services/merge-patients-data.service";
import { PanelModule } from "primeng/panel";
import { ConfirmationService, PrimeTemplate } from "primeng/api";
import {
    MergePatientProcessingEndpoint,
    PendingMatchingProfileMergePatientEndpoint
} from "../merge-patient/services/merge-processing.service";
import { UserHelperService } from "@main/users/user-helper.service";
import { DialogModule } from "primeng/dialog";
import { MergePatientDialogComponent } from "../merge-patient/merge-patient.dialog.component";
import { OverlayPanelModule } from "primeng/overlaypanel";
import { PendingMatchingProfilesDataService } from "./pending-matching-profiles.data.service";
import { PendingMatchingProfile } from "./pending-matching-profile";

@Component({
    selector: "pending-matching-profiles-dialog",
    standalone: true,
    imports: [
        CommonModule,
        MiproSharedModule,
        ButtonModule,
        MessagesModule,
        PanelModule,
        DialogModule,
        MergePatientDialogComponent,
        OverlayPanelModule,
        PrimeTemplate
    ],
    templateUrl: "./pending-matching-profiles.dialog.component.html",
    providers: [
        MergePatientsDataService,
        {
            provide: MergePatientProcessingEndpoint,
            useValue: PendingMatchingProfileMergePatientEndpoint
        },
        PendingMatchingProfilesDataService
    ]
})
export class PendingMatchingProfilesDialogComponent implements OnInit {
    protected readonly patientsNamingConvention = inject(NamingConventionService).endUserPlural;
    private readonly confirmationService = inject(ConfirmationService);
    private readonly userHelperService = inject(UserHelperService);
    private readonly pendingMatchingProfilesDataService = inject(PendingMatchingProfilesDataService);

    protected displayMergePatientDialog = signal(false);
    protected displayMatchingProfilesDialog = signal(false);
    protected prompts = signal([] as PendingMatchingProfile[]);
    protected visible = signal(false);
    protected sourcePatientId = this.userHelperService.patientId;
    protected targetPatientId = signal("");
    protected authToken = signal("");
    protected markingPrompts = signal(false);

    async ngOnInit() {
        if (this.userHelperService.isUser) {
            return;
        }

        const prompts = await this.pendingMatchingProfilesDataService.getPatientPrompts();
        this.prompts.set(prompts);
        this.visible.set(prompts.length > 0);
        this.displayMatchingProfilesDialog.set(prompts.length > 0);
    }

    protected onProfileSelected(patientId: string) {
        this.confirmationService.confirm({
            header: "Merge Patient Profile",
            message: "Are you sure you want to merge your profile with this one?",
            accept: async () => {
                this.targetPatientId.set(patientId);

                var approve = await this.pendingMatchingProfilesDataService.approvePendingMerge(patientId);

                this.authToken.set(approve.authToken);
                this.displayMergePatientDialog.set(true);
                this.displayMatchingProfilesDialog.set(false);
            }
        });
    }

    protected async reviewLater() {
        this.markingPrompts.set(true);
        await this.pendingMatchingProfilesDataService.markPatientPrompts();
        this.visible.set(false);
    }

    protected mergeDialogClosed() {
        window.location.reload();
    }

    protected deleteMatchingProfile(targetPatientId: string) {
        this.confirmationService.confirm({
            header: "Dismiss Matching Profile",
            message: "Are you sure this is not you?",
            accept: async () => {
                await this.pendingMatchingProfilesDataService.deleteMatchingProfile(targetPatientId);
                this.prompts.update(prompts => prompts.filter(p => p.id !== targetPatientId));
                this.visible.set(this.prompts.length > 0);
            }
        });
    }
}
